import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Circle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Component to handle map updates when center changes
function MapUpdater({ center }) {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  
  return null;
}

function ServiceAreaMap({ serviceArea, serviceRadius, onLocationChange }) {
  const [center, setCenter] = useState([39.8283, -98.5795]); // Default: center of USA
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Geocode the service area when it changes
  useEffect(() => {
    if (!serviceArea || serviceArea.trim() === '') {
      setError(null);
      return;
    }

    const geocodeAddress = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(serviceArea)}&format=json`
        );
        const data = await response.json();

        if (data.length > 0) {
          const result = data[0];
          const coords = [parseFloat(result.lat), parseFloat(result.lon)];
          setCenter(coords);
          onLocationChange?.(coords);
        } else {
          setError('Location not found. Please check the spelling.');
        }
      } catch (err) {
        setError('Error finding location. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    geocodeAddress();
  }, [serviceArea, onLocationChange]);

  // Convert miles to meters for Leaflet (which uses meters)
  const radiusInMeters = serviceRadius * 1609.34;

  return (
    <div style={{ marginTop: '20px' }}>
      {loading && <p style={{ color: '#0284c7', fontWeight: '600' }}>📍 Finding location...</p>}
      {error && <p style={{ color: '#dc2626', fontWeight: '600' }}>❌ {error}</p>}

      <div
        style={{
          height: '350px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #cbd5e1',
          marginBottom: '15px'
        }}
      >
        <MapContainer center={center} zoom={11} style={{ height: '100%', width: '100%' }}>
          <MapUpdater center={center} />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {!loading && (
            <>
              {/* Circle showing service area */}
              <Circle
                center={center}
                radius={radiusInMeters}
                pathOptions={{
                  color: '#0284c7',
                  fillColor: '#0284c7',
                  fillOpacity: 0.15,
                  weight: 2,
                }}
              >
                <Popup>
                  <div>
                    <strong>{serviceArea}</strong>
                    <br />
                    Coverage: {serviceRadius} miles
                  </div>
                </Popup>
              </Circle>

              {/* Center marker */}
              <Circle
                center={center}
                radius={300}
                pathOptions={{
                  color: '#0284c7',
                  fillColor: '#0284c7',
                  fillOpacity: 1,
                }}
              >
                <Popup>Service Area Center</Popup>
              </Circle>
            </>
          )}
        </MapContainer>
      </div>

      <div
        style={{
          background: '#f0f9ff',
          border: '1px solid #7dd3fc',
          borderRadius: '6px',
          padding: '12px',
          fontSize: '13px',
          color: '#0369a1'
        }}
      >
        📍 <strong>Service Area:</strong> {serviceArea || 'Not set'} | <strong>Radius:</strong> {serviceRadius} miles
      </div>
    </div>
  );
}

export default ServiceAreaMap;
